/**
 * RecoveryROIPanel (iter85) — Attribute revenue to recovery emails.
 *
 * Reads GET /api/admin/billing-abuse/recovery-roi?window=... and renders:
 *   • Headline: X emails sent, Y converted, Z% conversion rate, $ recovered
 *   • Per-reason table: which failure reason converts best
 *   • Recent conversion details: users who re-topped-up within 48h
 *
 * Data model: a "conversion" = same user had a successful paid auto-topup
 * transaction within 48h AFTER we dispatched a recovery email.
 */
import React, { useEffect, useState } from 'react';
import { TrendingUp, Target, DollarSign, Zap, Sparkles, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';

const REASON_LABELS = {
  card_error: 'Card declined',
  stripe_error: 'Stripe API error',
  no_payment_method: 'No card on file',
  no_customer: 'No Stripe customer',
  requires_action: 'SCA required',
  unknown: 'Unknown',
};

const formatCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return iso; }
};

const HeadlineTile = ({ icon: Icon, label, value, sub, tone, testId }) => {
  const toneClasses = {
    emerald: 'text-emerald-400 border-emerald-800/40 bg-emerald-950/20',
    slate: 'text-slate-100 border-[#1F2A4D] bg-[#0F1730]/60',
    amber: 'text-amber-300 border-amber-800/40 bg-amber-950/20',
  };
  return (
    <div className={`border rounded-md p-3 ${toneClasses[tone] || toneClasses.slate}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 opacity-80">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
};

const RecoveryROIPanel = ({ window: window_, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/admin/billing-abuse/recovery-roi', { params: { window: window_ } });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [window_, refreshKey]);

  if (loading && !data) {
    return (
      <Card data-testid="recovery-roi-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-100">
            <Target className="h-5 w-5 text-emerald-400" />
            Recovery ROI
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-slate-400 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Computing attribution…
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const {
    attribution_hours, emails_sent, successful_emails_sent,
    converted_users, conversion_rate_pct,
    recovered_revenue_cents, recovered_credits,
    by_reason = [], converted_details = [],
  } = data;

  const showEmpty = emails_sent === 0;

  return (
    <Card data-testid="recovery-roi-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-100">
          <Target className="h-5 w-5 text-emerald-400" />
          Recovery ROI
          {converted_users > 0 && (
            <Badge className="bg-emerald-900/40 text-emerald-200 border-emerald-800/40 text-[10px]">
              {formatCents(recovered_revenue_cents)} recovered
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Successful auto-topups by users within {attribution_hours}h of a recovery email → attributed as recovered revenue.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {showEmpty ? (
          <div className="text-sm text-slate-500 italic text-center py-6" data-testid="recovery-roi-empty">
            No recovery emails sent in this window yet. Send some via the &quot;Recover&quot; button below to start closing the loop.
          </div>
        ) : (
          <>
            {/* Headline tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <HeadlineTile
                icon={Sparkles} label="Emails sent" value={successful_emails_sent}
                sub={`${emails_sent} total attempts`}
                tone="slate" testId="roi-tile-sent"
              />
              <HeadlineTile
                icon={Zap} label="Users converted" value={converted_users}
                sub={`Within ${attribution_hours}h attribution`}
                tone={converted_users > 0 ? 'emerald' : 'slate'}
                testId="roi-tile-converted"
              />
              <HeadlineTile
                icon={TrendingUp} label="Conversion rate" value={`${conversion_rate_pct}%`}
                sub={successful_emails_sent > 0 ? `${converted_users} of ${successful_emails_sent} emails` : 'No successful sends yet'}
                tone={conversion_rate_pct >= 25 ? 'emerald' : conversion_rate_pct > 0 ? 'amber' : 'slate'}
                testId="roi-tile-rate"
              />
              <HeadlineTile
                icon={DollarSign} label="Revenue recovered" value={formatCents(recovered_revenue_cents)}
                sub={`${recovered_credits.toLocaleString()} credits`}
                tone={recovered_revenue_cents > 0 ? 'emerald' : 'slate'}
                testId="roi-tile-revenue"
              />
            </div>

            {/* Per-reason breakdown */}
            {by_reason.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">By failure reason</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-right">Emails</TableHead>
                      <TableHead className="text-right">Converted</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {by_reason.map((r) => (
                      <TableRow key={r.reason} data-testid={`roi-reason-row-${r.reason}`}>
                        <TableCell>
                          <span className="text-sm text-slate-100">{REASON_LABELS[r.reason] || r.reason}</span>
                        </TableCell>
                        <TableCell className="text-right text-slate-300">{r.emails_sent}</TableCell>
                        <TableCell className="text-right">
                          {r.converted > 0 ? (
                            <span className="text-emerald-300 font-medium">{r.converted}</span>
                          ) : (
                            <span className="text-slate-500">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={
                            r.conversion_rate_pct >= 25 ? 'bg-emerald-900/40 text-emerald-200 border-emerald-800/40 text-[10px]' :
                            r.conversion_rate_pct > 0 ? 'bg-amber-900/40 text-amber-200 border-amber-800/40 text-[10px]' :
                            'bg-slate-800 text-slate-400 border-slate-700 text-[10px]'
                          }>
                            {r.conversion_rate_pct}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.recovered_cents > 0 ? (
                            <span className="text-emerald-400 font-semibold">{formatCents(r.recovered_cents)}</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Recent conversions */}
            {converted_details.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Recent conversions</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Pack</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Converted</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {converted_details.map((d, i) => (
                      <TableRow
                        key={`${d.user_id}-${d.converted_at}-${i}`}
                        data-testid={`roi-conversion-row-${d.user_id}`}
                      >
                        <TableCell>
                          <div className="text-sm font-medium text-slate-100">{d.email || 'Unknown'}</div>
                          {d.full_name && <div className="text-[10px] text-slate-500">{d.full_name}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-800 text-slate-300 text-[10px]">
                            {REASON_LABELS[d.reason] || d.reason}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-300 capitalize text-sm">{d.pack_slug || '—'}</TableCell>
                        <TableCell className="text-right">
                          <span className="text-emerald-400 font-semibold">{formatCents(d.amount_cents)}</span>
                          {d.credits && (
                            <div className="text-[10px] text-slate-500">{d.credits.toLocaleString()} credits</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-slate-400 text-xs">
                          {formatRelative(d.converted_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecoveryROIPanel;
